import { css, keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { NoticeIcon } from 'rpComponents/Icons'
import { rpMediaQueries } from '../../core/theme'
import Card from '../Card'
import { DETAIL_CARD_GAP, DETAIL_CARD_SIZE } from '../layout/rpDecisionLayout'

// ─── Size constants ───────────────────────────────────────

const mCompactCardWidth = 19
const mCompactCardHeight = 31
const tCompactCardWidth = 41
const tCompactCardHeight = 66

const mDetailCardWidth = DETAIL_CARD_SIZE.mobile.width
const mDetailCardHeight = DETAIL_CARD_SIZE.mobile.height
const tDetailCardWidth = DETAIL_CARD_SIZE.tablet.width
const tDetailCardHeight = DETAIL_CARD_SIZE.tablet.height
const extraActionDisabledColor = 'rgba(255, 255, 255, 0.29)'

const getExtraActionColor = (
  theme: { rpTheme: { colors: { green: string; gold1: string } } },
  variant: 'buySixCard' | 'switchCards',
  isActive?: boolean,
  isEnabled?: boolean
) => {
  if (isEnabled === false) return extraActionDisabledColor
  if (variant === 'switchCards' || isActive) return theme.rpTheme.colors.green
  return theme.rpTheme.colors.gold1
}

// ─── Layout ───────────────────────────────────────────────

export const Container = styled.div`
  position: relative;
`

const CommonNotice = styled.div`
  position: absolute;
  bottom: calc(100% + 1px);

  ${rpMediaQueries.tablet} {
    bottom: calc(100% + 3px);
  }
`

export const Combination = styled(CommonNotice)`
  right: 4px;
`

export const StyledNoticeAboutSwitchCards = styled(CommonNotice)`
  left: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
`

export const IconNotice = styled(NoticeIcon)``

export const CardContainer = styled.div<{
  isDetailView: boolean
  hasTopOffset: boolean
}>`
  border-radius: 3px;
  border: 1px solid rgba(255, 255, 255, 0.29);
  padding: 1px;
  position: relative;

  ${({ isDetailView, hasTopOffset }) =>
    isDetailView &&
    `
    border-color: transparent;

    ${
      hasTopOffset
        ? `
      margin-top: 10px;

      ${rpMediaQueries.tablet} {
        margin-top: 18px;
      }

      ${rpMediaQueries.desktop} {
        margin-top: 24px;
      }
    `
        : ''
    }
  `}
`

export const CardContainerInside = styled.div<{
  count: number
  isDetailView: boolean
}>`
  position: relative;
  width: 100%;
  display: flex;

  min-height: ${({ isDetailView }) =>
    isDetailView ? `${mDetailCardHeight}px` : `${mCompactCardHeight}px`};

  ${rpMediaQueries.tablet} {
    min-height: ${({ isDetailView }) =>
      isDetailView ? `${tDetailCardHeight}px` : `${tCompactCardHeight}px`};
  }

  ${({ isDetailView }) =>
    isDetailView &&
    `
      gap: ${DETAIL_CARD_GAP.mobile}px;
      justify-content: flex-start;

      ${rpMediaQueries.desktop} {
        gap: ${DETAIL_CARD_GAP.desktop}px;
      }
    `}

  ${({ count, isDetailView }) =>
    isDetailView &&
    `
      width: ${
        count * mDetailCardWidth + (count - 1) * DETAIL_CARD_GAP.mobile
      }px;

      ${rpMediaQueries.tablet} {
        width: ${
          count * tDetailCardWidth + (count - 1) * DETAIL_CARD_GAP.tablet
        }px;
      }

      ${rpMediaQueries.desktop} {
        width: ${
          count * tDetailCardWidth + (count - 1) * DETAIL_CARD_GAP.desktop
        }px;
      }
    `}
`

// ─── Detail cards ─────────────────────────────────────────

const StyleCommonDetailCard = styled(Card)`
  width: ${mDetailCardWidth}px;
  height: ${mDetailCardHeight}px;
  border: 0.7px solid rgba(255, 255, 255, 0.29);
  border-radius: 3px;
  padding: 1px;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transform-style: preserve-3d;
  transform: rotateY(0deg);
  transition: opacity 0.3s ease;

  ${rpMediaQueries.tablet} {
    width: ${tDetailCardWidth}px;
    height: ${tDetailCardHeight}px;
  }
`

export const StyledDetailCard = styled(StyleCommonDetailCard, {
  shouldForwardProp: (prop) => !['darkened', 'isClickable'].includes(prop),
})<{
  darkened: boolean
  isClickable?: boolean
}>`
  opacity: ${({ darkened }) => (darkened ? 0.5 : 1)};
  filter: ${({ darkened }) => (darkened ? 'brightness(0.6)' : 'none')};
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: scale(1.05);
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const StyledExtraActionCard = styled(StyleCommonDetailCard, {
  shouldForwardProp: (prop) =>
    !['variant', 'isActive', 'isEnabled'].includes(prop),
})<{
  variant: 'buySixCard' | 'switchCards'
  isActive?: boolean
  isEnabled?: boolean
}>`
  border-color: ${({ theme, variant, isActive, isEnabled }) =>
    getExtraActionColor(theme, variant, isActive, isEnabled)};
  cursor: ${({ variant, isEnabled }) =>
    isEnabled && variant !== 'switchCards' ? 'pointer' : 'default'};
  pointer-events: ${({ isEnabled }) => (isEnabled ? 'auto' : 'none')};
  transition: border-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  svg {
    position: absolute;
    fill: ${({ theme, variant, isActive, isEnabled }) =>
      getExtraActionColor(theme, variant, isActive, isEnabled)};
    transition: opacity 0.3s ease, transform 0.3s ease, fill 0.3s ease;
    opacity: 0;
    transform: scale(0.8) rotateY(90deg);

    &.active {
      opacity: 1;
      transform: scale(1) rotateY(0deg);
    }

    ${rpMediaQueries.tablet} {
      height: 26px;
      width: 18px;
    }
  }

  svg.blocked {
    height: 12px;
    width: 12px;

    ${rpMediaQueries.tablet} {
      height: 18px;
      width: 18px;
    }
  }

  svg.blocked path,
  svg.blocked g,
  svg.blocked rect,
  svg.blocked circle {
    fill: ${extraActionDisabledColor};
  }

  svg.blocked line,
  svg.blocked path[stroke],
  svg.blocked rect[stroke],
  svg.blocked circle[stroke] {
    stroke: ${extraActionDisabledColor};
  }

  svg.blocked * {
    ${rpMediaQueries.tablet} {
      stroke-width: 1.8px;
    }
  }

  &:hover {
    opacity: 0.8;
  }
`

// ─── Compact cards ────────────────────────────────────────

export const StyledCompactCard = styled(Card)<{
  index: number
  count: number
}>`
  width: ${mCompactCardWidth}px;
  height: ${mCompactCardHeight}px;
  opacity: 0;
  flex-shrink: 0;

  img {
    width: auto;
    height: 100%;
    object-fit: cover;
  }

  ${({ index, count }) => {
    const spacing =
      count > 1 ? `calc((100% - ${mCompactCardWidth}px) / ${count - 1})` : '0px'

    const slideFade = keyframes`
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    `
    return css`
      margin-left: ${index === 0
        ? 0
        : `calc(-${mCompactCardWidth}px + ${spacing})`};
      animation: ${slideFade} 0.3s ease-out forwards;
      animation-delay: ${index * 100}ms;
    `
  }}

  ${rpMediaQueries.tablet} {
    width: ${tCompactCardWidth}px;
    height: ${tCompactCardHeight}px;

    ${({ index, count }) => {
      const spacing =
        count > 1
          ? `calc((100% - ${tCompactCardWidth}px) / ${count - 1})`
          : '0px'

      const slideFadeT = keyframes`
        from {
          opacity: 0;
          transform: translateY(-10px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      `
      return css`
        margin-left: ${index === 0
          ? 0
          : `calc(-${tCompactCardWidth}px + ${spacing})`};
        animation: ${slideFadeT} 0.4s ease-out forwards;
        animation-delay: ${index * 100}ms;
      `
    }}
  }
`
